import React, { useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../services/firebase";
import { useAuth } from "../../contexts/AuthContext";
import toast from "react-hot-toast";

const Container = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 3rem 2rem;
`;

const Title = styled.h1`
  margin-bottom: 2rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background: var(--color-accent);
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    background: var(--color-accent-hover);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Status = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: var(--color-bg);
  border-radius: 8px;
  white-space: pre-wrap;
`;

const TempImport = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const [status, setStatus] = useState("");

  const posts = [
    {
      title: "Identity in a Vacuumed Existence",
      excerpt:
        "A (very) personal exploration on identity formation and alienation.",
      category: "Essay",
      content: `This ramble begins with two poems written in an emotional frenzy.

## ORIGAMI

Folding in half, unfolding, creasing, with indents gingerly staked into the fibres.
Shaping into a flower, a student, a daughter, whatever you wish.

The world melts from images into words, into numbers, into a hunger -
to be torn tear into shreds, or,
to not exist at all.
Folding origami cranes: 1, 2, 3,...1000.
Identity is only tangible when shaped. 
And I only wish to be designed by my own hands. 

What are you, if not constructed on the brink of a divided existence?
Merely a reflection without a mirror, othered yet oneself, folding in,
all-consuming forces annihilate the soul in subterfuge to carve a waxed, wafer-smooth form of perfection.

What is the point of origami if it is nothing but a vacuumed conception?

## EXILE

Some days it feels like my very existence forbids my essence. 
I shudder, shudder at the mass of information I need to synthesise 
to perform my next theatrical act on a tightrope,

And then I retreat into the world of nothingness 
from the undertones and idiosyncrasies flooding my brain - 
Will there ever be a depth I can touch which is unsurpassable? 
A rawness of the human condition.
My entire existence forbids the fulfilment of living,
for every thought, every part of my body and heart and mind is constructed by elements 
outside the human limit in its essence. 

"Confess, lie, and you shall live."
And so I hover in a stasis of catharsis and conception, 
I am fashioned into existence like origami paper purposefully folded into perfection, 
and once designed, the creases will forever remain.

The end of exile is the end of being.

---

Flowery language plastered over a petty rant.

Summer exams had just ended, and I went around asking some people for their answers to a particular question. Long story short, someone got annoyed by my persistent questioning about an exam paper and made some rude and dismissive remarks. The next few hours of my day were characterised by me pacing back and forth in my room, harbouring an irrationally petty grudge against this person who probably had a difficult time with the paper and simply wanted to move on.

There were so many things I wanted to tell her. "My education does not just end when exams are over", "You could have expressed yourself more politely…" A more impulsive part of me wanted to flip her off. Yet, I recall myself standing in front of her in disbelief for a few moments, then walking away. Why?  I could have said something back to regain my dignity, and yet, I did not. There are days when I want to be myself without repercussions, and say things without a filter. However, whenever I open my mouth, I feel both unnaturally and naturally inclined to regulate myself.

At some point, I shifted from being a child who speaks and acts without thought to a teenager who is chained by thought. I have conditioned myself to think before I speak, to rationalise my actions before acting. And I wonder, is everyone born like this? Are we all born with our eyes plastered to a camera lens to perceive the world? 

We can never see the rawness of someone else's emotions (including our own) because no matter how hard we try to be vulnerable, real and authentic, emotions are never independent of other events. Actions are likewise a product of every single interaction and emotion dependent on external events which occurred before the present moment. Any expression one makes is filtered through a lens entangled in our human condition.

The 19th-century French sociologist Emile Durkheim coined the term 'anomie' in his classic study of suicide. Anomie in society or individuals refers to the condition of instability resulting from an internal breakdown of standards and societal values – "a feeling of alienation caused by the inability to believe in or to live up to the values of society" (Eriksen 2010, p.66). This breakdown of societal standards engenders a psychological state of emotional despair and emptiness due to a lack of institutionalised purpose.

Upon reflection, I felt anomie, for I was indeed in the state of mind of a person who has rejected all societal standards or obligations to maintain social bonds by conforming to expected regulated behaviour. I regretted my conformity to social expectations. 

There is a bittersweet fascination which lies in socialisation. It is a process of internalising the values systems and forms of behaviour upon which society is built. For example, religious commands like the Golden Rule universalise morals and conflate rules with natural inclinations which are aligned with modern societal standards. Our language is similarly internalised. We speak languages like English, French, Cantonese dialects or even colloquial slang like Singlish without much reflection or thought. Culture is by extension internalised. We refrain from being offensive – or more like – we learn to be polite to others because that is a part of our nurtured elementary manners and cultural customs. A common example of socialisation is how most people naturally face the lift door upon entering the lift.

The deeper we dig into this rabbit hole of internalising social conditioning from morality to culture to norms, the lines blur between the intuitive and the excess of social obligations pushed onto us – for better or for worse. In that case, is there any defined line between the conscious social roles we play and the people we "truly are"?

One of the most influential works discussing social identities through role enactment is Erving Goffman's The Presentation of Self in Everyday Life (1956). In Small Places, Large Issues (1995), Thomas Eriksen summarises Goffman's idea of impression management:

"Goffman's main point is the fact that there are social conventions defining everything we do as social creatures. Even to express the most powerful and sincere emotions, one has to follow specific, culturally defined rules prescribing how to express such emotions. Even the most spontaneous of acts must be channelled through a socially defined mode of expression if it is to be comprehensible" (Eriksen 2010, p. 56).

The creation of our social identities cannot be linearly mapped. There is something suffocating about knowing that "all I am and will ever be is origami paper; shaped by social forces beyond my control." Acting upon this realisation means that I either crumple or embrace my design. I guess the only silver lining is that embracing the complexities of our social identity makes our world and the people in it so much more intriguing.

**References:**

Britannica, T. Editors of Encyclopaedia (2020, May 27). anomie. Encyclopedia Britannica. https://www.britannica.com/topic/anomie

Eriksen, T. H. (2010). Small places, large issues: An introduction to social and cultural anthropology (Third edition). Pluto Press.

Goffman, E. (2007). The presentation of self in everyday life (Repr). Penguin Books.`,
      originalDate: new Date("2023-06-17").toISOString(),
      postedDate: new Date("2023-08-06").toISOString(),
    },
    {
      title: "NYC is frankly, an eyesore",
      excerpt:
        "An anthropological perspective of how urban landscapes reflect cultural values, with New York City being the prime case study.",
      category: "Essay",
      content: `New York is a concrete jungle,

Its haphazard architecture – each structure reaching out to find a place of its own in an obscure world. Like an individualist, every one of these buildings resists the motions of society. Only here you'd find the abomination of a Victorian-style church shoved next to an indomitable black rod you'd reluctantly call a skyscraper. It seems absurd to me.

*(5th Avenue)*

In Italy, France, London, and even in cities like Singapore; architecture is a subtle representation of our culture and societal constructs. Our values.

Land use regulation permits the demolishing of several historic or period architectural designs, sealing their significance in our modern world. 

New York, a funny place – creates period designs within a modern world. Who are we to plan the future to create a past that never existed? 

These New Yorkers. With no regard for history and preservation – they create skyscrapers so tall and so wide they become a canopy entrapping its inhabitants. Then came the emergence of wedding cake architecture; a policy in itself (enforced in the 1915s I believe). It forced these buildings to allow natural light to crawl into the dense concrete jungle. 

*(5th Avenue)*

Wedding cake architecture. Layers of decorated walls stacked up to the ceiling of the sky. 

A monument for the desire to achieve greatness, to create a place of one's own and a representation of the hope for a brighter future. An individual future.

> "In New York,  
> Concrete jungle where dreams are made of" – The Empire State of Mind

Incongruity permeates the atmosphere with unease.

But it's charming, isn't it? No other city is so divided and yet so united in individualism. 

Looking back at the metaphor 'concrete jungle' which is likened to New York City – I think it takes on a different and deeper meaning. 

**Concrete:**

The non-conforming nature of the city.
A place where one is able to set their foundation for a strong identity.
The charm of this place is that there is a lack of assimilation: The only assimilation one needs is to accept the non-conformity of everyone and everything in the city. You have to embrace who you are because there will be no one you can be. 

There is no template for a New Yorker because the template suggests you can be whoever you want. That may be disorientating for those who haven't solidified a strong enough identity to traverse the jungle of NYC but alas – to each their own.

*(Attachment)*

On a related note,

*(The High-Line to Hudson's Yard)*

Another example of individualism and audacity. It is a little funny how objectively, this is a spectacular walkway. However, this spectacular walkway is an uncomfortable disturbance to the residents of the surrounding buildings. Indeed, what a great idea to surround the walkway with commercial residential blocks. It was possible for me to peer through at least 4 levels worth of units at the level I was standing at. 

It is probably difficult to sell the units so near the High-Line and for good reason at that. Poor execution.`,
      originalDate: new Date("2022-06-08").toISOString(),
      postedDate: new Date("2023-05-12").toISOString(),
    },
    {
      title: "I Miss Falling in Love",
      excerpt: "About how social media has influenced our perception of love.",
      category: "Essay",
      content: `In your run-off-the-mill coming-of-age movie, picture the protagonist meeting a Manic Pixie Dream Girl (MPDG) on their journey of self-discovery, tumbling into a youthful romance. 

Nowadays, do we really 'tumble' into romance? Do we fall into romance? Or do we find a door and walk right into it? The advent of technology; more specifically, social media, has hurled us into a world where stumbling upon a potential romantic partner in an airport terminal is much more a fantasy than a possible reality. Such natural, organic encounters have been replaced by dating apps like Tinder and Bumble. 'Looking for love? Here we present to you a million potential partners you can pick and choose from like food in a supermarket!' That simile is somewhat intended to be derogatory – one can't deny that the proliferation of such dating platforms has (to an extent) perpetuated the commodification of people, reducing them to profiles. Consequentially, its creation has inadvertently amplified the belief that we can find 'the one' who 'ticks all the boxes'. 

That is not to say the notion of 'the one' was created by dating platforms. In my introduction to this chapter, I asked you to picture the typical film featuring a blossoming blissful romance – in the prime of one's youth. The long-standing film trope of an MPDG was only coined in the 21st century by Nathan Rabin in his review of Elizabethtown. Despite the G in the acronym, I believe this concept has morphed to be applied to both sexes but I will be using the traditional heterosexual dynamic for this essay. The MPDG manifests in all sorts of appearances but retains the general trope of being 'not like other girls', the quirky and eccentric deuteragonist; but also likeable in the way she may help the male protagonist in his transformation from naivety to enlightenment. This bubbly archetype is a staple of pop-culture films, books, and the like. The media we consume paints our narrative for the expectations of love. The MPDG trope has been criticised for the seeming lack of agency or personal voice. Laurie Penny sheds light on the real-world implications of the ubiquity of the MPDG – "Men grow up expecting to be the hero of their own story…Women grow up expecting to be the supporting actress of somebody else's." 

It is almost as if they were picked out from the supermarket, packaged with all the essentials and purpose to bestow the romantic partner with life lessons and unlock their potential.

And so, many of us may subconsciously embrace the feeling of love and recognise it through the lens of a cartoon character of sorts. We acknowledge acts of love and its expected dynamics through those reflected in films. 

And when we finally step into this world of love and relationships, we are met with missed expectations and a great sense of disappointment. We want to love and be loved, and we enforce our ideals through the perpetuated myths of love on television. This results in asymmetrical expectations between the sexes. We expect to be that protagonist – flawed, struggling, living in ignorance and anticipation for the day that someone will come along and help us find all the answers. That someone will be idiosyncratic, someone you never thought you'd ever fall in love with but there you go; the sparks are going off and you know you have found the one. They were pre-packaged like a Mcdonald's Happy Meal – made for you. 

In real life, we may blend this idea of stumbling into love and searching for our pre-packaged romance by vicariously living our lives like a movie. It is wonderful to obsess and become infatuated with someone, waiting for the day they will finally look your way and realise you were the one for them. Is infatuation the same as love? When do we realise these tropes have passed the threshold of intimacy into the realm of true romantic love?

> "Because love can burn like a cigarette  
> And leave you alone with nothing." – Lovers Rock, Tv Girl

Love burning like a cigarette reminded me of the idea of limerence. We put ourselves into the shoes of the protagonist of a movie, and subconsciously or consciously pick 'eye candy' to begin this coming-of-age movie. You glance at them bashfully in the hallways. You blush at the thought of their smile, the way they walk, the nape of their neck, and the way they chuckle. They sound just like the characters in love songs. They remind you of your favourite romance movie character.

In this fantastical longing, be careful to not get addicted. Limerence is the state of being obsessively infatuated with someone, usually accompanied by delusions of a desire for an intense romantic relationship with that person. It is rather difficult to differentiate between love and limerence. After all, with limerence – one adamantly proves to themselves that their romantic interest is "the one". In this fantastical disillusionment, infatuation can easily mirror the stages of falling in love through the obsessive rumination and lust you feel towards them. Limerence mistaken as love is all-consuming. At some point, one may become so deluded that they ignore the person's flaws or misconstrue them to be traits that you can 'fix'. "Together, you will be complete" – just like in the movies.

It is so easy to be in limerence with someone. But when the curtains close and you are full-fledged into a romantic relationship, you may take off your rose-tinted glasses as quickly as you had put them on. Your romantic interest's flaws are now on full display in their rawness. The 'halo effect' has diminished and the realisation that you cannot fix this person dawns upon them. They reinforce the notion that you will never be complete and never find 'the one'. 

We are, in fact, not characters in a movie. We are not profiles on Tinder or any other dating platform. We are raw, authentic individuals with identities unencumbered and much more than the traits reflected in film tropes.

The characterisation of a romantic partner has been so much easier with technology and media. We so desperately want to be the 'main character' and experience the romance we see in movies. We so desperately want to be complete. But the potential disillusionment which comes with our hopeless romantic ideas may lead us to destruction. 

'Love' can burn like a cigarette – suffocating, consuming, addictive, impermanent.`,
      originalDate: new Date("2023-01-20").toISOString(),
      postedDate: new Date("2023-05-12").toISOString(),
    },
  ];

  const handleImport = async () => {
    if (!currentUser) {
      toast.error("You must be logged in to import posts");
      return;
    }

    setImporting(true);
    setStatus("Starting import...\n");

    for (const post of posts) {
      try {
        setStatus((prev) => prev + `\nImporting "${post.title}"...`);

        await addDoc(collection(db, "posts"), {
          title: post.title,
          excerpt: post.excerpt,
          content: post.content,
          category: post.category,
          createdAt: serverTimestamp(),
          originalDate: post.originalDate,
          postedDate: post.postedDate,
          author: {
            uid: currentUser.uid,
            displayName: "Isabel Jade",
            photoURL: currentUser.photoURL,
          },
        });

        setStatus((prev) => prev + " ✓");
      } catch (error) {
        setStatus((prev) => prev + ` ✗ (${error.message})`);
        console.error("Error importing post:", error);
      }
    }

    setStatus((prev) => prev + "\n\nImport complete!");
    toast.success("Posts imported successfully!");

    setTimeout(() => {
      navigate("/");
    }, 2000);
  };

  if (!currentUser) {
    return (
      <Container>
        <p>You must be logged in to import posts.</p>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Import Existing Posts</Title>

      <p>This will import your 3 existing blog posts from jisdeu.com:</p>
      <ul>
        <li>Identity in a Vacuumed Existence</li>
        <li>NYC is frankly, an eyesore</li>
        <li>I Miss Falling in Love</li>
      </ul>

      <Button onClick={handleImport} disabled={importing}>
        {importing ? "Importing..." : "Import Posts"}
      </Button>

      {status && <Status>{status}</Status>}
    </Container>
  );
};

export default TempImport;
